import React from "react";
import { MdBarChart } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState } from "components/states";
import { QuestionDetail, QuestionsList } from "components/heartbeat/analytics";
import { formatNumber } from "utils/format";

/**
 * Question list + selected question analytics. Side by side from `lg`, stacked below.
 */
const QuestionAnalyticsPanel = ({
  questionAnalytics,
  loading,
  selectedQuestionId,
  onSelectQuestion,
  textResponses,
  loadingTextResponses,
  searchQuery,
  onSearchChange,
  sortOrder,
  onSortChange,
  onGenerateQuestionSummary,
  generatingQuestionSummary,
}) => {
  const questions = questionAnalytics?.questions || [];

  if (questions.length === 0) {
    if (!loading) return null;
    return (
      <div className="flex flex-col gap-5 lg:flex-row" aria-busy="true">
        <Skeleton className="h-64 w-full rounded-card lg:w-80 lg:shrink-0" />
        <Skeleton className="h-64 w-full rounded-card lg:flex-1" />
      </div>
    );
  }

  const selectedQuestion = questions.find((q) => q.questionId === selectedQuestionId);

  return (
    <section aria-label="Question analytics" className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <Card className="max-h-96 overflow-hidden lg:sticky lg:top-20 lg:max-h-[600px] lg:w-80 lg:shrink-0">
        <CardHeader
          className="border-b border-line p-4"
          title={
            <span className="flex items-center gap-2">
              <MdBarChart aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
              Questions ({questions.length})
            </span>
          }
          description={`${formatNumber(questionAnalytics.totalResponses)} total responses`}
        />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <QuestionsList
            questions={questions}
            selectedId={selectedQuestionId}
            onSelect={onSelectQuestion}
          />
        </div>
      </Card>

      <div className="min-w-0 flex-1">
        {selectedQuestionId ? (
          <QuestionDetail
            question={selectedQuestion}
            textResponses={textResponses}
            loadingTextResponses={loadingTextResponses}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
            onGenerateAISummary={() => onGenerateQuestionSummary(selectedQuestionId)}
            isGeneratingSummary={generatingQuestionSummary[selectedQuestionId]}
          />
        ) : (
          <Card padding="md">
            <EmptyState
              icon={MdBarChart}
              title="Select a Question"
              description="Choose a question to view its analytics."
            />
          </Card>
        )}
      </div>
    </section>
  );
};

export default QuestionAnalyticsPanel;
